// ship_routes_manager.jsx
// CRUD for vessel voyages (ship_routes + ship_route_stops) against the live
// navela-ship-routes API. Route fields are PATCHed; the itinerary is replaced
// whole via PUT /{id}/stops. Stop times are Puerto Rico local (AST, -04:00) — we
// keep the port wall-clock exactly, without browser-timezone conversion.

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Route, Plus, Pencil, Trash2, RefreshCw, MapPin, GripVertical,
  ChevronLeft, ChevronRight, CalendarDays, Ship,
} from 'lucide-react';
import { useNavela } from '../../authentication_components/navelaContext';
import { Modal, Field, inputClass, Button, Loading, EmptyState, ConfirmDialog, strOrNull } from './navela_ui';

const PAGE_SIZE = 25;
const AST = '-04:00'; // Navela pins all port times to Puerto Rico AST.

const intOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

// A stored AST instant ("2026-06-13T08:00:00-04:00") → "YYYY-MM-DDTHH:mm" for the
// datetime-local input, taking the wall-clock straight from the string (no Date
// conversion, so the port time never shifts by the admin's timezone).
function astToLocalInput(rfc) {
  if (!rfc) return '';
  const m = String(rfc).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : '';
}
// datetime-local "YYYY-MM-DDTHH:mm" → AST instant "…:00-04:00" (or null).
function localInputToAst(val) {
  if (!val) return null;
  const m = String(val).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}:00${AST}` : null;
}

const EMPTY_FORM = {
  vessel_id: '', title: '', voyage_code: '', cruise_line: '', nights: '',
  departure_date: '', return_date: '', embarkation_port: '', disembarkation_port: '', source_url: '',
};
const EMPTY_STOP = { port_name: '', country: '', arrival: '', departure: '' };

export default function ShipRoutesManager() {
  const { fetchRoutes, getRoute, createRoute, updateRoute, deleteRoute, replaceRouteStops, loading } = useNavela();

  const [routes, setRoutes] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [vesselFilter, setVesselFilter] = useState('');

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [stops, setStops] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingStops, setLoadingStops] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (opts = {}) => {
    try {
      const data = await fetchRoutes({
        vessel_id: vesselFilter || undefined,
        limit: PAGE_SIZE,
        offset: opts.offset ?? offset,
      });
      setRoutes(data?.routes ?? []);
      setTotal(data?.total_count ?? 0);
    } catch (err) {
      toast.error(err.message || 'Failed to load routes');
    }
  }, [fetchRoutes, vesselFilter, offset]);

  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); load({ offset: 0 }); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselFilter]);

  useEffect(() => {
    load({ offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setForm({ ...EMPTY_FORM, vessel_id: vesselFilter || '' });
    setStops([{ ...EMPTY_STOP }]);
    setEditing({});
  };

  const openEdit = async (r) => {
    setForm({
      vessel_id: r.vessel_id ?? '',
      title: r.title ?? '',
      voyage_code: r.voyage_code ?? '',
      cruise_line: r.cruise_line ?? '',
      nights: r.nights ?? '',
      departure_date: r.departure_date ?? '',
      return_date: r.return_date ?? '',
      embarkation_port: r.embarkation_port ?? '',
      disembarkation_port: r.disembarkation_port ?? '',
      source_url: r.source_url ?? '',
    });
    setEditing(r);
    setLoadingStops(true);
    try {
      const full = await getRoute(r.id);
      setStops((full?.stops ?? []).map((s) => ({
        port_name: s.port_name ?? '',
        country: s.country ?? '',
        arrival: astToLocalInput(s.arrival),
        departure: astToLocalInput(s.departure),
      })));
    } catch {
      setStops([]);
    } finally {
      setLoadingStops(false);
    }
  };

  const closeModal = () => { setEditing(null); setForm(EMPTY_FORM); setStops([]); };

  const addStop = () => setStops((s) => [...s, { ...EMPTY_STOP }]);
  const removeStop = (i) => setStops((s) => s.filter((_, idx) => idx !== i));
  const updateStop = (i, patch) => setStops((s) => s.map((st, idx) => (idx === i ? { ...st, ...patch } : st)));

  const stopsPayload = () => stops
    .filter((s) => s.port_name.trim() !== '')
    .map((s, i) => ({
      stop_order: i,
      port_name: s.port_name.trim(),
      country: strOrNull(s.country),
      arrival: localInputToAst(s.arrival),
      departure: localInputToAst(s.departure),
    }));

  const submit = async (e) => {
    e.preventDefault();
    const isEdit = editing && editing.id;
    if (!isEdit && (intOrNull(form.vessel_id) === null || intOrNull(form.vessel_id) <= 0)) {
      toast.error('Vessel ID is required'); return;
    }
    if (!form.departure_date) { toast.error('Departure date is required'); return; }

    setSaving(true);
    try {
      const routeFields = {
        title: strOrNull(form.title),
        voyage_code: strOrNull(form.voyage_code),
        cruise_line: strOrNull(form.cruise_line),
        nights: intOrNull(form.nights),
        departure_date: strOrNull(form.departure_date),
        return_date: strOrNull(form.return_date),
        embarkation_port: strOrNull(form.embarkation_port),
        disembarkation_port: strOrNull(form.disembarkation_port),
        source_url: strOrNull(form.source_url),
      };

      if (isEdit) {
        // Voyage fields via PATCH, then replace the itinerary in a second call.
        await updateRoute(editing.id, routeFields);
        await replaceRouteStops(editing.id, stopsPayload());
        toast.success('Route updated');
      } else {
        await createRoute({ vessel_id: intOrNull(form.vessel_id), ...routeFields, stops: stopsPayload() });
        toast.success('Route created');
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRoute(deleteTarget.id);
      toast.success('Route deleted');
      setDeleteTarget(null);
      const nextOffset = routes.length === 1 && offset >= PAGE_SIZE ? offset - PAGE_SIZE : offset;
      setOffset(nextOffset);
      load({ offset: nextOffset });
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const go = (nextOffset) => { setOffset(nextOffset); load({ offset: nextOffset }); };

  const portChain = (r) => {
    const a = r.embarkation_port, b = r.disembarkation_port;
    if (a && b) return `${a} → ${b}`;
    return a || b || '—';
  };

  const isEmpty = !loading && routes.length === 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <Route className="h-5 w-5 text-pr-navy-600" /> Ship Routes
          </h2>
          <p className="text-sm text-slate-500">{total.toLocaleString()} voyage{total === 1 ? '' : 's'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => load()} title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          <Button onClick={openNew}><Plus className="h-4 w-4" /> Add route</Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Ship className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={vesselFilter} onChange={(e) => setVesselFilter(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Filter by vessel ID" className={`${inputClass} w-52 pl-9`} />
        </div>
      </div>

      {loading && routes.length === 0 ? (
        <Loading label="Loading routes…" />
      ) : isEmpty ? (
        <EmptyState icon={Route} title="No routes match" hint="Add a voyage and its itinerary stops for a vessel." action={<Button onClick={openNew}><Plus className="h-4 w-4" /> Add route</Button>} />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Voyage</th>
                  <th className="px-4 py-3">Vessel</th>
                  <th className="px-4 py-3">Dates</th>
                  <th className="px-4 py-3">Route</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {routes.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{r.title || r.voyage_code || `Voyage #${r.id}`}</div>
                      <div className="text-xs text-slate-400">#{r.id}{r.nights ? ` · ${r.nights} nights` : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.vessel_name || `#${r.vessel_id}`}</td>
                    <td className="px-4 py-3 text-slate-500">
                      {r.departure_date ? (
                        <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-slate-400" />{r.departure_date}{r.return_date ? ` – ${r.return_date}` : ''}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{portChain(r)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(r)} className="rounded-lg p-2 text-slate-400 hover:bg-pr-navy-50 hover:text-pr-navy-600" title="Edit"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => setDeleteTarget(r)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
            <span>Page {page} of {pageCount}</span>
            <div className="flex items-center gap-1">
              <button disabled={offset === 0} onClick={() => go(Math.max(0, offset - PAGE_SIZE))} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
              <button disabled={page >= pageCount} onClick={() => go(offset + PAGE_SIZE)} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={editing !== null}
        title={editing && editing.id ? 'Edit route' : 'Add route'}
        subtitle={editing && editing.id ? `#${editing.id}` : 'Create a voyage and its itinerary'}
        onClose={closeModal}
        wide
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>{saving ? 'Saving…' : editing && editing.id ? 'Save changes' : 'Create route'}</Button>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Vessel ID" required={!(editing && editing.id)} hint={editing && editing.id ? 'Fixed for an existing voyage' : 'The vessel this voyage belongs to'}>
              <input type="number" min="1" className={inputClass} value={form.vessel_id} disabled={!!(editing && editing.id)} onChange={(e) => setForm({ ...form, vessel_id: e.target.value })} placeholder="123" />
            </Field>
            <Field label="Title">
              <input className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="7-Night Eastern Caribbean" />
            </Field>
            <Field label="Voyage code">
              <input className={inputClass} value={form.voyage_code} onChange={(e) => setForm({ ...form, voyage_code: e.target.value })} />
            </Field>
            <Field label="Cruise line" hint="Matched or created by name">
              <input className={inputClass} value={form.cruise_line} onChange={(e) => setForm({ ...form, cruise_line: e.target.value })} placeholder="Royal Caribbean" />
            </Field>
            <Field label="Departure date" required>
              <input type="date" className={inputClass} value={form.departure_date} onChange={(e) => setForm({ ...form, departure_date: e.target.value })} />
            </Field>
            <Field label="Return date">
              <input type="date" className={inputClass} value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} />
            </Field>
            <Field label="Nights">
              <input type="number" min="0" className={inputClass} value={form.nights} onChange={(e) => setForm({ ...form, nights: e.target.value })} />
            </Field>
            <Field label="Embarkation port">
              <input className={inputClass} value={form.embarkation_port} onChange={(e) => setForm({ ...form, embarkation_port: e.target.value })} placeholder="San Juan, Puerto Rico" />
            </Field>
            <Field label="Disembarkation port">
              <input className={inputClass} value={form.disembarkation_port} onChange={(e) => setForm({ ...form, disembarkation_port: e.target.value })} />
            </Field>
            <Field label="Source URL" className="sm:col-span-2">
              <input className={inputClass} value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://…" />
            </Field>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <MapPin className="h-3.5 w-3.5" /> Itinerary stops <span className="font-normal normal-case text-slate-400">(times are Puerto Rico local · AST)</span>
              </span>
              <Button variant="secondary" type="button" onClick={addStop} className="px-3 py-1.5 text-xs"><Plus className="h-3.5 w-3.5" /> Add stop</Button>
            </div>
            {loadingStops ? (
              <Loading label="Loading stops…" />
            ) : stops.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-xs text-slate-400">No stops yet. Add the ports this voyage calls at.</p>
            ) : (
              <div className="space-y-2">
                {stops.map((s, i) => (
                  <div key={i} className="grid grid-cols-1 items-end gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3 sm:grid-cols-[auto,1.4fr,1fr,1fr,1fr,auto]">
                    <div className="flex items-center gap-1 text-slate-400"><GripVertical className="h-4 w-4" /><span className="text-xs font-bold">{i + 1}</span></div>
                    <Field label="Port">
                      <input className={inputClass} value={s.port_name} onChange={(e) => updateStop(i, { port_name: e.target.value })} placeholder="San Juan, Puerto Rico" />
                    </Field>
                    <Field label="Country">
                      <input className={inputClass} value={s.country} onChange={(e) => updateStop(i, { country: e.target.value })} placeholder="PR" />
                    </Field>
                    <Field label="Arrival">
                      <input type="datetime-local" className={inputClass} value={s.arrival} onChange={(e) => updateStop(i, { arrival: e.target.value })} />
                    </Field>
                    <Field label="Departure">
                      <input type="datetime-local" className={inputClass} value={s.departure} onChange={(e) => updateStop(i, { departure: e.target.value })} />
                    </Field>
                    <button type="button" onClick={() => removeStop(i)} className="mb-1 rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600" title="Remove stop"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-1.5 text-xs text-slate-400">Leave arrival empty for the embarkation port and departure empty for the final port.</p>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete route?"
        message={deleteTarget ? `“${deleteTarget.title || `Voyage #${deleteTarget.id}`}” and its itinerary stops will be permanently removed.` : ''}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
